import React from "react";
import styles from './visualiser.module.scss';
import { useSpring, animated, easings, useSpringValue } from "@react-spring/web";

interface SoundContainerProps {
  height: number;
  children?: React.ReactNode;
}

const SoundContainer: React.FC<SoundContainerProps> = ({height, children}) => {
  return (
    <div className={styles.soundContainer} style={{ height }}>
      {children}
    </div>
  );
}

interface BarProps {
  width: string;
  color: any;
  delayInSeconds: number;
  durationInSeconds: number;
  initialScaleY: number;
  scaleY: number;
  cornerRadius?: number;
  verticalAlignment: "top" | "center" | "bottom";
}

const Bar: React.FC<BarProps> = ({ width, cornerRadius, color, delayInSeconds, durationInSeconds, scaleY, initialScaleY, verticalAlignment }) => {
  const barAnimation = useSpring({
    from: { transform: `scaleY(${initialScaleY})` },
    to: { transform: `scaleY(${scaleY || 0})` },
    loop: {
      reverse: true,
    },
    delay: delayInSeconds * 1000,
    config: { 
      duration: durationInSeconds * 1000,
      easing: easings.linear,
    },
  });

  return (
    <animated.div
      className={styles.bar}
      style={{
        width,
        borderRadius: cornerRadius ? `${cornerRadius}px` : undefined,
        backgroundColor: color,
        transformOrigin: `${verticalAlignment} center`,
        ...barAnimation
      }}
    />
  );
}

export const SoundVisualizer = (props: {
  disabled: boolean;
  volumeLevel: number;
  barCount: number;
  barWidth: string;
  height: number;
  color: string;
  durationInSeconds: number;
  cornerRadius?: number;
  verticalAlignment?: "top" | "center" | "bottom";
}) => {
  // generate mappable items from bar count
  const items = Array.from(Array(props.barCount), (_, i) => i);

  const backgroundColor = useSpringValue('#FF284F');
  const colorStops = ['#FF284F', '#FF439B', '#FF45CF', '#309BE0', '#00A5B9'];

  React.useEffect(() => {
    backgroundColor.start({
      to: async (next) => {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          for (const col of colorStops) {
            await next(col)
          }
        }
      },
      config: { duration: 4000, easing: easings.linear },
    })
  }, [])

  return (
    <SoundContainer height={props.height}>
      {items.map((_, index) => (
        <Bar
          key={`bar-${index}`}
          delayInSeconds={Math.random() * 1}
          durationInSeconds={props.durationInSeconds + 0.2 * Math.random()}
          initialScaleY={props.disabled ? 0 : 0.1}
          scaleY={props.disabled ? 0 : Math.random() * 0.9 * (props.volumeLevel || 0) + 0.1}
          color={backgroundColor}
          width={props.barWidth}
          cornerRadius={props.cornerRadius}
          verticalAlignment={props.verticalAlignment || "bottom"}
        />
      ))}
    </SoundContainer>
  );
};
